import { Box, Button, IconButton, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { RootStateType } from "../../../redux-store/reducers";
import { useEffect, useState } from "react";
import { PaginationObj } from "../../investorOverview/types";
import { isModuleAllowed } from "../../../utils/utilityFunctions";
import BreadCrum from "../../../lib/BreadCrum";
import { SearchIcon } from "../../customSVGs";
import { DialogCloseIcon } from "../../../assets/SvgIcons";
import FamilyListTable from "./FamilyListTable";
import { FamilyForRiaFeeSetupObj, GetFamiliesForRiaFeeSetupResponseBody } from "../../../redux-store/types/onBoarding";
import { getFamiliesForRiaFeeSetup } from "../../../redux-store/actions/reports";
import { showError } from "../../../redux-store/actions/auth";

const DEBOUNCE_DELAY = 300

export default function RiaFeeSetup(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);

    // State
    const [isLoading, setLoading] = useState<boolean>(true)
    const [searchText, setSearchText] = useState<string>("")
    const [tableState, setTableState] = useState<PaginationObj & { debouncedSearchText: string }>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0,
        debouncedSearchText: "",
    })
    const [tableData, setTableData] = useState<Array<FamilyForRiaFeeSetupObj>>([])
    const [selectedRow, setSelectedRow] = useState<FamilyForRiaFeeSetupObj | null>(null)

    const selectedFamilyId = selectedRow?.family_id ?? ""

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'RIA Fee Setup',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        }
    ] : [])

    const fetchFamilyList = async ({
        rowsPerPage,
        currentPage,
        search = ""
    }: {
        rowsPerPage: number;
        currentPage: number;
        search: string;
    }): Promise<GetFamiliesForRiaFeeSetupResponseBody | null> => {

        const payload = {
            rmId: `${iwappRmId}`,
            searchInput: search,
            currentPage: currentPage + 1,
            rowsPerPage
        }

        const response = await (dispatch(getFamiliesForRiaFeeSetup(payload)) as unknown as Promise<GetFamiliesForRiaFeeSetupResponseBody>)

        if (!response) {
            return null
        }

        if (!response?.isSuccess) {
            dispatch(showError(response?.message || "Data not found"))

            return null
        }

        return response
    }

    useEffect(() => {
        let cancelled = false

        const fetchData = async () => {
            try {
                setLoading(true)
                const response = await fetchFamilyList({
                    rowsPerPage: tableState.rowsPerPage,
                    currentPage: tableState.currentPage,
                    search: tableState.debouncedSearchText,
                })
                if (cancelled) return
                if (response) {
                    setTableData(response?.families ?? [])
                    setTableState((prev) => (
                        prev.totalRows === response.count ? prev : { ...prev, totalRows: response.count }
                    ))
                }
            } catch (error) {
                console.log('Error: ', error)
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }
        fetchData()

        return () => {
            cancelled = true
        }
    }, [tableState.currentPage, tableState.rowsPerPage, tableState.debouncedSearchText])

    useEffect(() => {
        const debounceTimeoutId = setTimeout(() => {
            setTableState((prev) => (
                (prev.debouncedSearchText === searchText && prev.currentPage === 0)
                    ? prev
                    : { ...prev, debouncedSearchText: searchText, currentPage: 0 }
            ))
        }, DEBOUNCE_DELAY)
        return () => clearTimeout(debounceTimeoutId)
    }, [searchText])

    const handleViewHistoryClick = () => {
        history.push(`${pathName}/history`)
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    }

    const handleTableRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTableState((prev) => ({
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            currentPage: 0,
        }))
    }

    const handleTablePageChange = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        page: number,
    ) => {
        setTableState((prev) => ({
            ...prev,
            currentPage: page,
        }))
    }

    const handleRadioToggle = (id: string) => {
        if (selectedFamilyId === id) {
            setSelectedRow(null)
            return
        }
        const row = tableData.find((row: FamilyForRiaFeeSetupObj) => row.family_id === id) ?? null
        setSelectedRow(row)
    }

    const handleModifyFeeSetupClick = () => {
        if (!selectedRow) return
        history.push(`${pathName}/${selectedFamilyId}/modify`)
    }

    return (
        <>
            <Box sx={{ marginBottom: "0.5rem" }}>
                <BreadCrum array={routerPath} />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: { xs: "flex-start", md: "space-between" },
                    alignItems: { xs: "stretch", md: "center" },
                    rowGap: "2rem",
                    marginBottom: "2rem"
                }}
            >
                <Typography
                    variant="h1"
                    sx={{ color: theme.palette.text.primary }}
                >
                    RIA Fee Setup
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleViewHistoryClick}
                    sx={{
                        ...theme.typography.body_600,
                        color: theme.palette.primary[500],
                        borderColor: theme.palette.primary[500],
                        borderWidth: "1.5px",
                        borderRadius: "10px",
                        width: { xs: "100%", md: "auto" },
                        py: { xs: "0.3rem", sm: "0.4rem" },
                        px: { xs: "1rem", sm: "1.5rem", md: "2rem" },
                        "&:hover": {
                            backgroundColor: `${theme.palette.primary[500]}1A`,
                            borderWidth: "1.5px",
                        },
                    }}
                >
                    View History
                </Button>
            </Box>
            <Box
                sx={{
                    padding: '25px',
                    borderRadius: '15px',
                    background: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: { xs: "flex-start", md: "space-between" },
                        alignItems: { xs: "stretch", md: "center" },
                        rowGap: "2rem",
                        marginBottom: "2rem"
                    }}
                >
                    <Typography
                        variant='h2'
                        sx={{ color: theme.palette.text.primary }}
                    >
                        List of Families
                    </Typography>
                    <Button
                        variant={selectedRow ? "contained" : "outlined"}
                        onClick={handleModifyFeeSetupClick}
                        disabled={!selectedRow}
                        sx={{
                            ...theme.typography.body_500,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.primary[500],
                            borderRadius: "10px",
                            width: { xs: "100%", md: "auto" },
                            py: { xs: "0.4rem", sm: "0.5rem" },
                            px: { xs: "1rem", sm: "1.5rem", md: "2rem" },
                            "&:hover": {
                                backgroundColor: theme.palette.primary[800],
                            },
                        }}
                    >
                        Fee Setup
                    </Button>
                </Box>
                <TextField
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Clear search"
                                    sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                    onMouseDown={handleClearSearch}
                                >
                                    <DialogCloseIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: '10px',
                            height: '50px',
                            border: `1px solid ${theme.palette.text.secondary}B2`,
                            '& fieldset': {
                                border: 'none',
                            },
                            '& input': {
                                paddingLeft: '10px',
                                ...theme.typography.body_500,
                                color: theme.palette.text.primary,
                                '::placeholder': {
                                    ...theme.typography.subtitle_400,
                                    color: theme.palette.text.secondary,
                                    opacity: 1,
                                },
                            },
                        },
                    }}
                    value={searchText}
                    placeholder="Search..."
                    onChange={handleSearchTextChange}
                />
                <Box sx={{ marginTop: "2rem" }}>
                    <FamilyListTable
                        isLoading={isLoading}
                        tableData={tableData}
                        tableState={tableState}
                        handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                        handleTablePageChange={handleTablePageChange}
                        selectedRow={selectedRow}
                        onRadioToggle={handleRadioToggle}
                    />
                </Box>
            </Box>
        </>
    )
}